import { Command } from 'commander'
import pc from 'picocolors'
import { existsSync, mkdirSync, writeFileSync } from 'fs'
import { join } from 'path'

export const initCommand = new Command('init')
  .description('Create a new Brixon presentation project')
  .argument('[name]', 'Project name', 'my-presentation')
  .option('-f, --force', 'Overwrite existing directory')
  .action(async (name: string, options: { force?: boolean }) => {
    const projectDir = join(process.cwd(), name)

    // Check if directory exists
    if (existsSync(projectDir) && !options.force) {
      console.error(pc.red(`Error: Directory "${name}" already exists.`))
      console.error(pc.dim('Use --force to overwrite.'))
      process.exit(1)
    }

    console.log(pc.cyan(`Creating new presentation: ${name}`))

    // Create directory structure
    mkdirSync(join(projectDir, 'src'), { recursive: true })
    mkdirSync(join(projectDir, 'public'), { recursive: true })

    // Create brixon.config.ts
    writeFileSync(
      join(projectDir, 'brixon.config.ts'),
      `import { defineConfig } from '@brixon-group/presentation-sdk'

export default defineConfig({
  presentation: {
    slug: '${name}',
    title: '${toTitleCase(name)}',
    subtitle: 'A Brixon Presentation',
    clientName: 'Client Name',
  },
  build: {
    outDir: 'dist',
  },
  dev: {
    port: 3000,
  },
})
`
    )

    // Create presentation.tsx
    writeFileSync(
      join(projectDir, 'src', 'presentation.tsx'),
      `'use client'

import {
  PresentationWrapper,
  Hero,
  Content,
  CTA,
  useTracking,
} from '@brixon-group/presentation-sdk'

const sections = [
  { id: 'hero', label: 'Introduction', order: 1, type: 'hero' as const },
  { id: 'challenge', label: 'The Challenge', order: 2, type: 'content' as const },
  { id: 'solution', label: 'Our Solution', order: 3, type: 'content' as const },
  { id: 'cta', label: 'Next Steps', order: 4, type: 'cta' as const },
]

export default function Presentation() {
  const { trackEngagement } = useTracking({
    presentationId: '${name}',
    sections,
  })

  return (
    <PresentationWrapper title="${toTitleCase(name)}" sections={sections}>
      <Hero
        title="${toTitleCase(name)}"
        subtitle="A Strategic Proposal"
        clientName="Client Name"
        year="${new Date().getFullYear()}"
      />

      <Content
        id="challenge"
        label="The Challenge"
        headline="Current Situation"
        description="Describe the challenges your client is facing..."
      />

      <Content
        id="solution"
        label="Our Solution"
        headline="How We Can Help"
        description="Describe your proposed solution..."
      />

      <CTA
        headline="Ready to get started?"
        subline="Let's discuss next steps"
        buttonText="Get in Touch"
        buttonHref="mailto:hello@example.com"
      />
    </PresentationWrapper>
  )
}
`
    )

    // Create package.json
    writeFileSync(
      join(projectDir, 'package.json'),
      JSON.stringify(
        {
          name: name,
          version: '0.1.0',
          private: true,
          scripts: {
            dev: 'brixon dev',
            build: 'brixon build',
            deploy: 'brixon deploy',
          },
          dependencies: {
            '@brixon-group/presentation-sdk': '^0.0.1',
            react: '^19.0.0',
            'react-dom': '^19.0.0',
          },
          devDependencies: {
            typescript: '^5.7.0',
            '@types/react': '^19.0.0',
          },
        },
        null,
        2
      )
    )

    // Create tsconfig.json
    writeFileSync(
      join(projectDir, 'tsconfig.json'),
      JSON.stringify(
        {
          compilerOptions: {
            target: 'ES2020',
            lib: ['dom', 'dom.iterable', 'ES2020'],
            allowJs: true,
            skipLibCheck: true,
            strict: true,
            noEmit: true,
            esModuleInterop: true,
            module: 'ESNext',
            moduleResolution: 'bundler',
            resolveJsonModule: true,
            isolatedModules: true,
            jsx: 'react-jsx',
          },
          include: ['src/**/*.ts', 'src/**/*.tsx', 'brixon.config.ts'],
          exclude: ['node_modules'],
        },
        null,
        2
      )
    )

    // Create CLAUDE.md
    writeFileSync(
      join(projectDir, 'CLAUDE.md'),
      `# ${toTitleCase(name)} Presentation

## Overview

Brixon presentation project using @brixon-group/presentation-sdk.

## Structure

- \`src/presentation.tsx\` - Main presentation component
- \`brixon.config.ts\` - Presentation configuration
- \`public/\` - Static assets (images, PDFs)
- \`.github/workflows/deploy.yml\` - Auto-deploy workflow

## Commands

\`\`\`bash
npm run dev      # Local development preview
npm run build    # Build for production
npm run deploy   # Deploy to platform (requires BRIXON_API_KEY)
\`\`\`

## Deployment Workflow

### Automatic (recommended)

1. Add \`BRIXON_API_KEY\` secret to GitHub repository settings
2. Push to main branch - workflow deploys automatically

### Manual

\`\`\`bash
BRIXON_API_KEY=your-key npm run deploy
\`\`\`

## SDK Installation (for new projects)

The SDK is hosted on GitHub Packages. Create \`.npmrc\`:

\`\`\`
@brixon-group:registry=https://npm.pkg.github.com
//npm.pkg.github.com/:_authToken=\${GITHUB_TOKEN}
\`\`\`

Then: \`npm install @brixon-group/presentation-sdk\`

## Components

| Component | Purpose |
|-----------|---------|
| \`Hero\` | Full-screen title section |
| \`Content\` | General content with headline/description |
| \`CTA\` | Call-to-action with button |
| \`Section\` | Generic trackable wrapper |

### Analytics Hook

\`\`\`tsx
const { trackEngagement } = useTracking({ presentationId: 'slug', sections })
trackEngagement('button_click', { target: 'cta' })
\`\`\`

## Editing Guide

1. Modify content in \`src/presentation.tsx\`
2. Register new sections in \`sections\` array (id must match component)
3. Static assets go in \`public/\` (reference as \`/filename.png\`)

Use proper German: ä, ö, ü, ß (not ae, oe, ue, ss).
`
    )

    // Create GitHub Actions workflow
    mkdirSync(join(projectDir, '.github', 'workflows'), { recursive: true })
    writeFileSync(
      join(projectDir, '.github', 'workflows', 'deploy.yml'),
      `name: Deploy Presentation

on:
  push:
    branches: [main]
  workflow_dispatch:

jobs:
  deploy:
    name: Build and Deploy
    runs-on: ubuntu-latest
    steps:
      - uses: actions/checkout@v4
      - uses: actions/setup-node@v4
        with:
          node-version: '20'
          cache: 'npm'
      - run: npm ci
      - run: npx brixon build
      - run: npx brixon deploy
        env:
          BRIXON_API_KEY: \${{ secrets.BRIXON_API_KEY }}
`
    )

    console.log(pc.green('\n✓ Project created successfully!'))
    console.log(pc.dim('\nNext steps:'))
    console.log(pc.white(`  cd ${name}`))
    console.log(pc.white('  npm install'))
    console.log(pc.white('  npm run dev'))
    console.log(pc.dim('\nFor deployment:'))
    console.log(pc.dim('  1. Add BRIXON_API_KEY secret to your GitHub repo'))
    console.log(pc.dim('  2. Push to main branch to auto-deploy'))
  })

function toTitleCase(str: string): string {
  return str
    .replace(/[-_]/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase())
}
